/* eslint-disable indent */
'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import ThemeToggle from '../ui/theme-toggle';
import LanguageSelector from '../ui/language-selector';
import { useLanguage } from '@/app/context/language-context';
import { useTheme } from '@/app/components/theme-provider';
import { Menu, X } from 'lucide-react';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const pathname = usePathname();
  const currentPage = pathname === '/' ? 'home' : 'other';

  // Check if we're on the home page
  const isHomePage = pathname === '/';

  // Split links into left and right sections
  const leftLinks = [
    { name: t('rent'), url: '/properties/rent' },
    { name: t('buy'), url: '/properties/buy' },
    { name: t('offPlan'), url: '/properties/off-plan' },
    { name: t('aboutUs'), url: '/about' },
    { name: t('newsArticles'), url: '/articles' },
  ];

  const rightLinks = [
    { name: t('blog'), url: '/blog' },
    { name: t('agent'), url: '/agents' },
    { name: t('service'), url: '/services' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Close the mobile menu only if scrolled more than 100px
      if (isMobileMenuOpen && window.scrollY > 100) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]); // Dependency on isMobileMenuOpen

  const getLogoSrc = () => {
    // Priority: mobile menu open
    if (isMobileMenuOpen) {
      return theme === 'light'
        ? language === 'zh'
          ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_black_ch_no_bg.webp'
          : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_black_eng_no_bg.webp'
        : language === 'zh'
          ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
          : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
    }

    // Dark theme → always white logo
    if (theme === 'dark') {
      return language === 'zh'
        ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
        : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
    }

    // Light theme cases
    if (theme === 'light') {
      if (currentPage === 'home') {
        // On homepage, use black logo only if scrolled
        return isScrolled
          ? language === 'zh'
            ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_black_ch_no_bg.webp'
            : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_black_eng_no_bg.webp'
          : language === 'zh'
            ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
            : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
      } else {
        // On all other pages → always black logo
        return language === 'zh'
          ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_black_ch_no_bg.webp'
          : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_black_eng_no_bg.webp';
      }
    }

    // Fallback (shouldn't reach here)
    return language === 'zh'
      ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
      : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
  };

  // Determine text color based on page, scroll position, and theme
  const getTextColor = () => {
    if (isScrolled) {
      return 'text-gray-700 hover:text-primarycolor dark:text-gray-200';
    }

    if (isHomePage) {
      return 'text-white hover:text-primarycolor dark:hover:text-secondarycolor';
    }

    return theme === 'light'
      ? 'text-gray-700 hover:text-primarycolor'
      : 'text-white hover:text-primarycolor';
  };

  // Determine mobile menu button color
  const getMobileButtonColor = () => {
    if (isMobileMenuOpen) {
      return theme === 'light' ? 'text-black' : 'text-white';
    }

    if (isScrolled) {
      return 'text-gray-700 hover:text-primarycolor dark:text-gray-200';
    }

    if (isHomePage) {
      return 'text-white hover:text-primarycolor';
    }

    return theme === 'light'
      ? 'text-gray-700 hover:text-primarycolor'
      : 'text-white hover:text-primarycolor';
  };

  const isActiveLink = (url) => pathname.startsWith(url);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isMobileMenuOpen
          ? theme === 'light'
            ? 'bg-white shadow-lg'
            : 'bg-gray-900 shadow-lg'
          : isScrolled
            ? 'bg-white shadow-lg dark:bg-gray-900'
            : 'bg-transparent'
      }`}
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex items-center justify-between h-20'>
          {/* Left side navigation */}
          <div className='flex items-center lg:justify-start justify-center'>
            {/* Mobile menu button */}
            <div className='lg:hidden mr-4'>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-md ${getMobileButtonColor()}`}
                aria-label='Toggle mobile menu'
              >
                {isMobileMenuOpen ? (
                  <X className='h-6 w-6' />
                ) : (
                  <Menu className='h-6 w-6' />
                )}
              </button>
            </div>

            <nav className='hidden lg:flex space-x-4 sm:space-x-6'>
              {leftLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.url}
                  className={`text-sm/8 font-semibold transition-colors ${getTextColor()} ${isActiveLink(link.url) ? 'border-b-2 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor' : ''}`}
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
            </nav>
          </div>

          {/* Logo - centered */}
          <div className='flex justify-center'>
            <Link href='/' className='flex-shrink-0'>
              <Image
                src={getLogoSrc()}
                alt='X-Space Real Estate'
                width={100}
                height={60}
                className='h-8 sm:h-10 w-auto'
                priority
              />
            </Link>
          </div>

          {/* Right side navigation */}
          <div className='flex items-center justify-end'>
            <nav className='hidden lg:flex items-center space-x-8'>
              {rightLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.url}
                  className={`text-sm/8 font-semibold transition-colors ${getTextColor()} ${isActiveLink(link.url) ? 'border-b-2 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor' : ''}`}
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
              <div className='flex items-center space-x-2'>
                <LanguageSelector currentPage={currentPage} />
                <ThemeToggle />
              </div>
              <Link
                href='/contact-us'
                className='bg-primarycolor hover:bg-secondarycolor text-white hover:text-black px-4 py-2 rounded-md text-sm font-medium transition-colors'
              >
                {t('contactUs').toUpperCase()}
              </Link>
            </nav>

            {/* Mobile language and theme toggles */}
            <div className='flex items-center lg:hidden'>
              <LanguageSelector currentPage={currentPage} />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className='lg:hidden bg-white dark:bg-gray-900 shadow-lg'
          >
            <div className='px-4 pt-2 pb-6 space-y-2'>
              {[...leftLinks, ...rightLinks].map((link) => (
                <Link
                  key={link.name}
                  href={link.url}
                  // className='block py-2 font-semibold text-gray-700 hover:text-primarycolor dark:text-gray-200'
                  className={`block py-2 font-semibold transition-colors ${
                    isActiveLink(link.url)
                      ? 'border-l-4 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor pl-2'
                      : 'text-gray-700 hover:text-primarycolor dark:text-gray-200'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name.toUpperCase()}
                </Link>
              ))}
              <div className='pt-4'>
                <Link
                  href='/contact-us'
                  className='block w-full font-semibold bg-primarycolor hover:bg-secondarycolor text-white hover:text-black px-4 py-2 rounded-md text-center text-sm transition-colors'
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('contactUs').toUpperCase()}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
